// The one place the browser is asked for a microphone.
//
// Three components used to do this themselves — useAudioRecorder,
// planning/VoiceField and the Member Story composer — and they had already
// drifted: different error wording, and only one of them survived a permission
// prompt that is never answered. Per CODING_STANDARDS ("when two enforcement
// points could drift, extract"), the request and the error vocabulary live
// here and nowhere else.

// getUserMedia does NOT reject when the user ignores or dismisses the browser's
// permission prompt — the promise simply never settles. Without a deadline the
// caller sits in "requesting" forever: a spinning button, no error, and no way
// out but a page reload. Observed on the Member Story interview, 2026-09-03.
// Generous enough that someone actually reading the prompt is not cut off.
export const PERMISSION_DEADLINE_MS = 30_000

/**
 * Ask for the microphone, and give up if the prompt is never answered.
 *
 * Resolves with a MediaStream, or rejects with an Error whose `name` is either
 * the browser's own (`NotAllowedError`, `NotFoundError`, …) or `TimeoutError`
 * when the deadline passed. Pass the rejection to `describeMicrophoneError`.
 */
export function requestMicrophone({ deadlineMs = PERMISSION_DEADLINE_MS } = {}) {
  const request = navigator.mediaDevices.getUserMedia({ audio: true })
  let timedOut = false

  // If the prompt is answered AFTER we gave up, release the microphone —
  // otherwise the mic LED stays on with nothing listening to it.
  request
    .then((late) => {
      if (timedOut) late.getTracks().forEach((track) => track.stop())
    })
    .catch(() => {})

  return Promise.race([
    request,
    new Promise((_, reject) => {
      setTimeout(() => {
        timedOut = true
        const error = new Error("microphone permission prompt was never answered")
        error.name = "TimeoutError"
        reject(error)
      }, deadlineMs)
    }),
  ])
}

/**
 * Turn a microphone failure into something a member can act on.
 *
 * Every message names the way forward, because the alternative — "access was
 * blocked", full stop — leaves someone mid-form with nothing to do.
 */
export function describeMicrophoneError(error, fallback = "Something went wrong.") {
  if (!error) return fallback

  switch (error.name) {
    case "NotAllowedError":
    case "SecurityError":
      return "Microphone access is blocked. Allow it from the padlock or camera icon in your browser's address bar, then tap the mic again."
    case "NotFoundError":
    case "OverconstrainedError":
      return "No microphone was found on this device."
    case "NotReadableError":
      return "Your microphone is in use by another app. Close the other app and tap the mic again."
    case "TimeoutError":
      return "We never heard back about microphone access. Check for a permission prompt in your browser, then tap the mic again."
    case "AbortError":
      return "Recording was interrupted."
    default:
      return error.message || fallback
  }
}
